/* K MANNAM v24 — PDP display only; never writes variant ID, quantity, cart prices or shipping.
   Scoped per form. Works with the theme's variant:changed / quantity:changed hooks,
   direct quantity input, section replacement and the legacy inner-HTML sticky clone. */
(() => {
  'use strict';
  if (customElements.get('km-pdp-summary')) return;
  const instances = new Set();
  const textFromMarkup = value => {
    const node = document.createElement('template');
    node.innerHTML = String(value == null ? '' : value);
    return node.content.textContent || '';
  };
  const formatMoney = (cents, config) => {
    if (!Number.isSafeInteger(cents) || cents < 0) return '—';
    const format = textFromMarkup(config.moneyFormat || '');
    const token = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const modes = {
      amount:[2,',','.'], amount_no_decimals:[0,',','.'],
      amount_with_comma_separator:[2,'.',','], amount_no_decimals_with_comma_separator:[0,'.',','],
      amount_with_apostrophe_separator:[2,"'",'.'], amount_with_space_separator:[2,' ','.'],
      amount_no_decimals_with_space_separator:[0,' ','.'], amount_with_period_and_space_separator:[2,' ','.']
    };
    if (token && modes[token[1]]) {
      const [precision, separator, decimal] = modes[token[1]];
      const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
      const result = whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator) + (fraction ? decimal + fraction : '');
      return format.replace(token[0], result);
    }
    try { return new Intl.NumberFormat('en-US', {style:'currency', currency:config.currency || 'USD'}).format(cents / 100); }
    catch (_) { return '$' + (cents / 100).toFixed(2); }
  };
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  class KMPdpSummary extends HTMLElement {
    connectedCallback() { queueMicrotask(() => { if (this.isConnected && !this.abort) this.init(); }); }
    init() {
      const source = this.querySelector('[data-km-pdp-summary-config]');
      this.root = this.closest('form.product-form') || this.closest('.atc_wrapper');
      if (!source || !this.root) return;
      try { this.config = JSON.parse(source.textContent); }
      catch (error) { console.warn('K MANNAM PDP: invalid summary config', error); return; }
      this.variants = this.config.variants || {};
      this.isClone = this.root.matches('.atc_wrapper');
      this.root.classList.add('km-pdp-layout-v24');
      this.scope = this.root.closest('[data-section-type="product"]');
      this.selectedId = this.config.selectedId;
      this.abort = new AbortController();
      const opts = { signal:this.abort.signal };
      const onVariant = event => {
        // A quick-view / another product instance must not overwrite this product.
        if (event.target.closest?.('[data-section-type="product"]') !== this.scope) return;
        const sourceForm = event.target.closest?.('form.product-form');
        if (sourceForm && sourceForm !== this.root && !this.isClone) return;
        const variant = event.detail && event.detail.variant;
        if (variant && Object.prototype.hasOwnProperty.call(this.variants, String(variant.id))) {
          this.selectedId = String(variant.id);
          this.renderPrices(); this.updateTotal(); this.stripButtonTotals();
        } else if (!variant && (!sourceForm || sourceForm === this.root || this.isClone)) {
          this.selectedId = null;
          this.renderPrices(); this.updateTotal(); this.stripButtonTotals();
        }
      };
      (this.scope || this.root).addEventListener('variant:changed', onVariant, opts);
      this.root.addEventListener('quantity:changed', () => this.updateTotal(), opts);
      this.root.addEventListener('input', event => {
        if (event.target.matches('[name="quantity"]')) this.updateTotal();
      }, opts);
      this.root.addEventListener('change', event => {
        if (event.target.matches('[name="quantity"]')) this.updateTotal();
        else if (event.target.matches('[name="id"]')) this.schedule(true);
        else if (event.target.matches('.product-form__single-selector')) this.schedule(false);
      }, opts);
      // Legacy drawer synchronizes quantities through jQuery without emitting input/change.
      this.root.addEventListener('click', event => {
        if (event.target.closest('[data-action="increase-picker-quantity"],[data-action="decrease-picker-quantity"]')) {
          clearTimeout(this.quantityTimer);
          this.quantityTimer = setTimeout(() => {
            for (const item of instances) {
              if (item.scope === this.scope && item.config.productId === this.config.productId) item.updateTotal();
            }
          }, 0);
        }
      }, opts);
      // Observe child insertions only: do not use broad global style/price mutation observers.
      this.observer = new MutationObserver(records => {
        if (records.some(record => record.target.closest?.('.product-form__add-button'))) this.stripButtonTotals();
      });
      this.observer.observe(this.root,{childList:true,subtree:true});
      instances.add(this);
      this.syncFromControls(); this.renderPrices(); this.updateTotal(); this.stripButtonTotals();
      this.setAttribute('data-km-ready','');
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null; this.observer?.disconnect();
      clearTimeout(this.syncTimer); clearTimeout(this.quantityTimer);
      instances.delete(this);
    }
    schedule(readMaster = false) {
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => {
        if (!this.isConnected) return;
        if (readMaster) this.syncFromControls();
        this.renderPrices(); this.updateTotal(); this.stripButtonTotals();
      },0);
    }
    syncFromControls() {
      // The native variant event fires before its master select updates; defer reads on change.
      const master = this.root.querySelector('[name="id"]');
      if (master) this.selectedId = Object.prototype.hasOwnProperty.call(this.variants, String(master.value)) ? String(master.value) : null;
    }
    renderPrices() {
      const prices = this.root.querySelector('[data-km-pdp-prices]');
      if (!prices) return;
      const variant = this.variants[this.selectedId];
      prices.hidden = !variant;
      if (!variant) { prices.replaceChildren(); return; }
      prices.style.removeProperty('display');
      const current = make('div','km-pdp-prices__current');
      const compare = Number(variant.compare), price = Number(variant.price);
      const sale = Number.isFinite(compare) && compare > price && compare > 0;
      if (sale) {
        const percent = Math.round((compare - price) / compare * 100);
        const promo = make('span','km-pdp-prices__discount',percent + '%');
        promo.setAttribute('data-km-pdp-discount','');
        promo.setAttribute('aria-label',percent + '% ' + (this.config.offLabel || 'off'));
        current.append(promo);
      }
      const priceSpan = make('span','km-pdp-prices__sale');
      priceSpan.append(make('span','visually-hidden',this.config.saleLabel || 'Price'),make('bdi','',textFromMarkup(variant.priceText) || formatMoney(price,this.config)));
      current.append(priceSpan);
      const children = [current];
      if (sale) {
        const old = make('div','km-pdp-prices__original');
        const strike = make('s');
        strike.append(make('bdi','',textFromMarkup(variant.compareText) || formatMoney(compare,this.config)));
        old.append(make('span','visually-hidden',this.config.regularLabel || 'Original price'),strike);
        children.push(old);
      }
      prices.replaceChildren(...children);
    }
    updateTotal() {
      const variant = this.variants[this.selectedId];
      const input = this.root.querySelector('[name="quantity"]');
      // No selector means one unit. An explicit zero/empty/invalid editable quantity is not silently rewritten.
      const raw = input && input.type !== 'hidden' ? String(input.value).trim() : '1';
      const quantity = /^\d+$/.test(raw) ? Number(raw) : NaN;
      const total = variant && Number.isSafeInteger(quantity) && quantity > 0 ? Number(variant.price) * quantity : NaN;
      const value = formatMoney(total,this.config);
      this.root.querySelectorAll('[data-km-pdp-total]').forEach(node => {
        if (node.textContent !== value) node.textContent = value;
      });
    }
    stripButtonTotals() {
      this.root.querySelectorAll('.product-form__add-button .btn__price,.product-form__add-button [id="dynamic-price"]').forEach(node => node.remove());
    }
  }
  customElements.define('km-pdp-summary',KMPdpSummary);
})();
